import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeftIcon, 
  CheckIcon,
  PlayIcon,
  Cog6ToothIcon,
  ClockIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const MethodologySelector = () => {
  const [selectedMethodology, setSelectedMethodology] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();
  const { setMethodology } = useProject();
  const { updateUser } = useAuth();

  const methodologies = [
    {
      id: 'scrum',
      name: 'Scrum',
      description: 'Metodología ágil con sprints cortos y roles definidos',
      icon: '🏃‍♂️',
      color: 'blue',
      duration: '1-4 semanas por sprint',
      roles: ['Product Owner', 'Scrum Master', 'Development Team'],
      artifacts: ['Product Backlog', 'Sprint Backlog', 'Increment'],
      ceremonies: ['Sprint Planning', 'Daily Standup', 'Sprint Review', 'Retrospective'],
      benefits: [
        'Entrega frecuente de valor',
        'Adaptabilidad a cambios',
        'Mejora continua',
        'Transparencia total',
        'Colaboración efectiva'
      ],
      bestFor: 'Equipos que necesitan entregas regulares y adaptabilidad',
      workflow: [
        'Product Owner prioriza el Product Backlog',
        'Sprint Planning: selección de tareas para el sprint',
        'Daily Standups: sincronización diaria',
        'Sprint Review: demostración de resultados',
        'Retrospective: mejora del proceso'
      ]
    },
    {
      id: 'kanban',
      name: 'Kanban',
      description: 'Flujo continuo con límites de trabajo en progreso',
      icon: '📋',
      color: 'green',
      duration: 'Flujo continuo',
      roles: ['Team Lead', 'Developers', 'QA'],
      artifacts: ['Kanban Board', 'Work Items', 'Metrics'],
      ceremonies: ['Daily Standup', 'Retrospective'],
      benefits: [
        'Flexibilidad máxima',
        'Visualización clara del flujo',
        'Reducción de desperdicios',
        'Mejora continua',
        'Menos overhead'
      ],
      bestFor: 'Equipos con trabajo continuo y cambios frecuentes',
      workflow: [
        'Visualizar el flujo de trabajo',
        'Limitar el trabajo en progreso',
        'Gestionar el flujo',
        'Hacer políticas explícitas',
        'Mejorar colaborativamente'
      ]
    },
    {
      id: 'lean',
      name: 'Lean',
      description: 'Eliminación de desperdicios y optimización de valor',
      icon: '⚡',
      color: 'purple',
      duration: 'Iteraciones cortas',
      roles: ['Lean Manager', 'Team Members', 'Stakeholders'],
      artifacts: ['Value Stream Map', 'Waste Analysis', 'Improvement Plan'],
      ceremonies: ['Gemba Walks', 'Kaizen Events', 'Value Stream Reviews'],
      benefits: [
        'Eliminación de desperdicios',
        'Optimización de valor',
        'Mejora continua',
        'Enfoque en el cliente',
        'Eficiencia operativa'
      ],
      bestFor: 'Organizaciones enfocadas en eficiencia y optimización',
      workflow: [
        'Identificar valor para el cliente',
        'Mapear el flujo de valor',
        'Crear flujo continuo',
        'Implementar pull system',
        'Buscar perfección continua'
      ]
    },
    {
      id: 'agile',
      name: 'Agile',
      description: 'Enfoque iterativo e incremental para desarrollo',
      icon: '🔄',
      color: 'orange',
      duration: '1-4 semanas por iteración',
      roles: ['Product Manager', 'Team Lead', 'Developers'],
      artifacts: ['Product Roadmap', 'User Stories', 'Iteration Plan'],
      ceremonies: ['Iteration Planning', 'Daily Standup', 'Demo', 'Retrospective'],
      benefits: [
        'Entrega incremental',
        'Colaboración estrecha',
        'Respuesta rápida a cambios',
        'Calidad mejorada',
        'Satisfacción del cliente'
      ],
      bestFor: 'Equipos de desarrollo que buscan flexibilidad y colaboración',
      workflow: [
        'Planificación de iteración',
        'Desarrollo colaborativo',
        'Testing continuo',
        'Demo y feedback',
        'Retrospectiva y mejora'
      ]
    },
    {
      id: 'waterfall',
      name: 'Waterfall',
      description: 'Metodología secuencial con fases bien definidas',
      icon: '🌊',
      color: 'indigo',
      duration: 'Varias semanas por fase',
      roles: ['Project Manager', 'Business Analyst', 'Developers', 'QA'],
      artifacts: ['Requirements Document', 'Design Document', 'Test Plan'],
      ceremonies: ['Requirements Review', 'Design Review', 'Code Review', 'Testing'],
      benefits: [
        'Planificación detallada',
        'Documentación completa',
        'Control de calidad',
        'Gestión de riesgos',
        'Cumplimiento de estándares'
      ],
      bestFor: 'Proyectos con requisitos estables y equipos grandes',
      workflow: [
        'Análisis de requisitos',
        'Diseño del sistema',
        'Implementación',
        'Testing y validación',
        'Despliegue y mantenimiento'
      ]
    },
    {
      id: 'custom',
      name: 'Personalizada',
      description: 'Crea tu propia metodología combinando elementos',
      icon: '🛠️',
      color: 'gray',
      duration: 'Flexible',
      roles: ['Personalizables'],
      artifacts: ['Configurables'],
      ceremonies: ['Personalizables'],
      benefits: [
        'Adaptación total a tu equipo',
        'Flexibilidad máxima',
        'Mejores prácticas combinadas',
        'Evolución continua',
        'Optimización específica'
      ],
      bestFor: 'Equipos experimentados que quieren crear su propio proceso',
      workflow: [
        'Define tu proceso ideal',
        'Selecciona herramientas',
        'Configura workflows',
        'Establece métricas',
        'Itera y mejora'
      ]
    }
  ];

  const handleSelectMethodology = async (methodologyId) => {
    setSelectedMethodology(methodologyId);
    setShowDetails(true);
  };

  const handleApplyMethodology = async () => {
    try {
      await setMethodology(selectedMethodology);
      await updateUser({ methodology: selectedMethodology });
      toast.success(`Metodología ${methodologies.find(m => m.id === selectedMethodology)?.name} aplicada exitosamente`);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Error al aplicar la metodología');
    }
  };

  const selectedMethod = methodologies.find(m => m.id === selectedMethodology);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">P</span>
              </div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Seleccionar Metodología</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!showDetails ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Elige tu metodología de trabajo
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Selecciona la metodología que mejor se adapte a tu equipo y proyecto. 
                Puedes cambiar esta configuración en cualquier momento.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {methodologies.map((method, index) => (
                <motion.div
                  key={method.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`card cursor-pointer transition-all duration-200 ${
                    selectedMethodology === method.id
                      ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'hover:shadow-medium'
                  }`}
                  onClick={() => handleSelectMethodology(method.id)}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-16 h-16 bg-${method.color}-100 dark:bg-${method.color}-900 rounded-xl flex items-center justify-center`}>
                        <span className="text-3xl">{method.icon}</span>
                      </div>
                      {selectedMethodology === method.id && (
                        <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                          <CheckIcon className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {method.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {method.description}
                    </p>
                    
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {method.duration}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <UserGroupIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {method.roles.length} roles principales
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <button className="w-full btn-secondary text-sm">
                        Ver detalles
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {selectedMethodology && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <button
                  onClick={handleApplyMethodology}
                  className="btn-primary text-lg px-8 py-3"
                >
                  Aplicar {methodologies.find(m => m.id === selectedMethodology)?.name}
                </button>
              </motion.div>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            {/* Header de detalles */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowDetails(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <ArrowLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
                <div className={`w-12 h-12 bg-${selectedMethod.color}-100 dark:bg-${selectedMethod.color}-900 rounded-xl flex items-center justify-center`}>
                  <span className="text-2xl">{selectedMethod.icon}</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedMethod.name}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {selectedMethod.description}
                  </p>
                </div>
              </div>
              <button
                onClick={handleApplyMethodology}
                className="btn-primary flex items-center space-x-2"
              >
                <PlayIcon className="h-4 w-4" />
                <span>Aplicar Metodología</span>
              </button>
            </div>

            {/* Detalles de la metodología */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Información básica */}
              <div className="space-y-6">
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Información Básica
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Duración de iteraciones
                      </label>
                      <p className="text-gray-900 dark:text-white">{selectedMethod.duration}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Ideal para
                      </label>
                      <p className="text-gray-900 dark:text-white">{selectedMethod.bestFor}</p>
                    </div>
                  </div>
                </div>

                {/* Roles */}
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Roles Principales
                  </h3>
                  <div className="space-y-2">
                    {selectedMethod.roles.map((role, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                        <span className="text-gray-900 dark:text-white">{role}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Artefactos */}
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Artefactos Principales
                  </h3>
                  <div className="space-y-2">
                    {selectedMethod.artifacts.map((artifact, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-900 dark:text-white">{artifact}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Flujo de trabajo y beneficios */}
              <div className="space-y-6">
                {/* Flujo de trabajo */}
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Flujo de Trabajo
                  </h3>
                  <div className="space-y-3">
                    {selectedMethod.workflow.map((step, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
                            {index + 1}
                          </span>
                        </div>
                        <p className="text-gray-900 dark:text-white">{step}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ceremonias */}
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Ceremonias/Eventos
                  </h3>
                  <div className="space-y-2">
                    {selectedMethod.ceremonies.map((ceremony, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <CalendarIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-900 dark:text-white">{ceremony}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Beneficios */}
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Beneficios Principales
                  </h3>
                  <div className="space-y-2">
                    {selectedMethod.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <CheckIcon className="h-4 w-4 text-success-500" />
                        <span className="text-gray-900 dark:text-white">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Acciones */}
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={() => setShowDetails(false)}
                className="btn-secondary"
              >
                Volver a la selección
              </button>
              <button
                onClick={handleApplyMethodology}
                className="btn-primary flex items-center space-x-2"
              >
                <Cog6ToothIcon className="h-4 w-4" />
                <span>Aplicar {selectedMethod.name}</span>
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default MethodologySelector;
